import type { InkStroke } from '../../domain/types';
import { markSent, pendingLogs, letzteStation } from '../../persistence/examLog';
import type { Store } from '../../persistence/port';
import { readExam } from '../../persistence/readExam';
import { buildTaskIndex, loadAssessment } from '../../persistence/repos';
import type {
  CandidateRow,
  ClientRow,
  ExamLogRow,
  ExaminerRow,
  ClassicKennungRow,
} from '../../persistence/rows';
import type { Station, Task } from '../../domain/types';
import { lohntWiederholung, type ServerClient } from '../client';
import { istNetzausfall } from '../verbindung';
import { baueBewertungen, type Bewertungsstapel } from './bewertungen';
import { rohkennung } from './kennung';
import type {
  ClassicZeilenbewertung,
  ClassicClientRequest,
  ClassicLogAntwort,
  ClassicLogRumpf,
  ClassicAufgabenbewertung,
} from './typen';

/**
 * Der Sendelauf zum Classic-Server: `POST /rest/log`.
 *
 * ⚠️ **Dieser eine Aufruf ist zweierlei zugleich** — Übertragung der Bewertungen **und**
 * Herzschlag des Geräts. Der Server legt den Eintrag eines Geräts erst an, wenn ein solcher
 * Rumpf eintrifft; ohne ihn sieht der Prüfungsleiter das iPad in seiner Konsole überhaupt
 * nicht, auch wenn darauf längst bewertet wird. Deshalb geht er **immer** hinaus, auch mit
 * leeren Listen.
 *
 * ## Die Quittung
 *
 * Der Server spiegelt den Rumpf zurück, aber nur mit den Ratings, die er speichern konnte. Es
 * gibt keine `rejected`-Liste und keine Zähler wie bei Relay: **ein fehlendes Rating ist die
 * Ablehnung.** Als übertragen gilt ein Rating erst, wenn `originalID` **und** `lastUpdate`
 * beide stimmen — hat der Prüfer während des Fluges weitergeschrieben, trägt die Bewertung
 * inzwischen ein anderes `lastUpdate`, und der Stempel darf nicht greifen.
 *
 * ⚠️ **Eine Logzeile wird erst gestempelt, wenn jedes Rating quittiert ist, zu dem sie
 * beiträgt.** Eine `ANSWER_RATING`-Zeile steckt in ihrem Zeilenbewertung und in der Summe des
 * Aufgabenbewertungen; stempelte die Zeilenbewertung allein, bliebe eine verlorene Aufgabenbewertung-Quittung
 * für immer unbemerkt — beim nächsten Takt wäre nichts mehr offen, und die Aufgabensumme auf
 * dem Server bliebe veraltet.
 *
 * Der Zeitgeber liegt beim Aufrufer, nicht hier: so lässt sich der Lauf prüfen, ohne auf Uhren
 * zu warten — dieselbe Bauart wie `sync.ts` und `autosave.ts`.
 */

/** Höchstens so viele Ratings je Rumpf, wie am alten Gerät. */
const STAPEL_TASKS = 3;
const STAPEL_ANSWERS_ALLEIN = 30;
const STAPEL_ANSWERS_MIT_TASKS = 5;

/**
 * Obergrenze der Runden je Lauf. Der Nutzer will „bis alles übertragen wurde"; ohne Grenze
 * liefe ein Lauf bei einem Server, der stets dasselbe zurückgibt, endlos.
 */
const HOECHSTENS_RUNDEN = 50;

export interface SendeErgebnis {
  /** Wie viele Protokollzeilen der Server behalten hat. */
  uebertragen: number;
  /** Wie viele liegengeblieben sind, weil sie sich nicht zurückschreiben lassen. */
  liegengeblieben: number;
  /** Eine Nachricht des Prüfungsleiters an dieses Gerät, oder `null`. */
  nachricht: string | null;
}

export interface ClassicSendeDeps {
  store: Store;
  client: ServerClient;
  /**
   * Das Dateilog. `stufe` bestimmt, unter welcher Konsolenfunktion die Zeile am Kabel in
   * Xcode erscheint (s. `platform/appLog.ts`); in der Datei sieht jede Zeile gleich aus.
   */
  log: (text: string, stufe?: 'warn' | 'error') => Promise<void>;
  /**
   * `appVersion` in der Form der alten App: `{Fassung}@{Plattform}v{OS}` (`2.1.15@iOSv18.6`).
   * `model` ist die Hardware-Kennung (`iPad13,18`).
   */
  geraet: { deviceID: string; deviceName: string; appVersion: string; model?: string };
  /** Ladestand in Prozent. Von aussen, damit der Lauf ohne Gerät prüfbar bleibt. */
  batterie?: () => Promise<number>;
  /**
   * Was zu tun ist, wenn der Server Dateien anfordert (`clientRequests`).
   *
   * Von aussen, aus demselben Grund wie `batterie`: das Sammeln und Hochladen kennt die
   * Ablage des Geräts, dieser Lauf hier soll sie nicht kennen müssen
   * (`features/.../anforderungen.ts` setzt es zusammen). Fehlt sie — im Browser etwa —, wird
   * die Anforderung nur protokolliert.
   */
  erfuelleAnforderungen?: (arten: ClassicClientRequest[]) => Promise<void>;
  /**
   * Gehen in diesem Lauf Bewertungen mit? Fehlt es: ja. Der Takt reicht `false`, wenn
   * `auto_upload` ruht — dann geht nur der Herzschlag mit leeren Listen hinaus, und der
   * Prüfungsleiter sieht das Gerät weiter in seiner Konsole.
   */
  mitDaten?: () => boolean;
  /**
   * Zeichnet eine Handskizze als PNG-Data-URL (`platform/skizzePng.ts`). Von aussen, weil es
   * eine Leinwand braucht; fehlt es, geht `commentImage` als `null` hinaus.
   */
  skizze?: (strokes: InkStroke[]) => string | null;
  /** Die Einstellungen in Classics Schlüsseln (`classicConf`). Fehlt es: `{}`. */
  conf?: () => Promise<Record<string, string>>;
  /** Bildschirmhelligkeit 0…1, `-1` wenn unbekannt — wie die alte App. */
  helligkeit?: () => Promise<number>;
  jetzt?: () => Date;
  zufall?: () => string;
}

/** Die Namensform, die Classic im Herzschlag erwartet. */
interface Person {
  id: number;
  firstname: string;
  lastname: string;
  /** Nur am Prüfling. */
  matriculationnumber?: string;
}

async function personVon(
  store: Store,
  tabelle: 'Examiner' | 'Candidate',
  kennung: string | null,
): Promise<Person | null> {
  if (!kennung) return null;
  const id = rohkennung(kennung);
  if (id === null) return null;
  const [zeile] = await store.all<ExaminerRow | CandidateRow>(tabelle, { id: kennung });
  if (!zeile) return null;
  const person: Person = { id, firstname: zeile.firstName, lastname: zeile.lastName };
  const matrikel = (zeile as CandidateRow).matriculationNumber;
  if (tabelle === 'Candidate' && matrikel) person.matriculationnumber = matrikel;
  return person;
}

/**
 * Den Rückstand nach (Prüfung, Prüfling) ordnen — je Paar braucht es einen eigenen
 * Bewertungsstand.
 */
function nachPruefling(offen: ExamLogRow[]): Map<string, ExamLogRow[]> {
  const karte = new Map<string, ExamLogRow[]>();
  for (const z of offen) {
    if (!z.examID || !z.candidateID) continue;
    const schlüssel = z.examID + '|' + z.candidateID;
    const bisher = karte.get(schlüssel);
    if (bisher) bisher.push(z);
    else karte.set(schlüssel, [z]);
  }
  return karte;
}

/**
 * Der laufende Durchgang — **modulweit**, nicht je Sender.
 *
 * ⚠️ **Takt und „Jetzt übertragen" sind zwei Aufrufer, nicht einer.** Die Einstellungen legen
 * bei jedem Tippen einen neuen Sender an (`features/sheets/ServerGroup.tsx`), der Takt hält
 * seinen eigenen (`classic/takt.ts`). Ein Merker im Abschluss schützte deshalb nur gegen sich
 * selbst. Zwei Läufe übereinander lesen denselben Rückstand — `storeDate` entsteht erst mit der
 * Quittung — und legen am Classic-Server zu jeder Bewertung zwei Zeilen an.
 */
let laeuft: Promise<SendeErgebnis> | null = null;

export function createClassicSender(deps: ClassicSendeDeps) {
  const jetzt = deps.jetzt ?? (() => new Date());
  const zufall = deps.zufall ?? (() => String(Math.random()));

  /** Fortlaufend absteigend, wie am alten Gerät: negative Kennungen sind nie Serverkennungen. */
  let kennungszaehler = 0;
  const naechsteId = () => {
    kennungszaehler -= 1;
    return kennungszaehler;
  };

  /**
   * Aufgabenindex, Prüfung und gemerkte Rohkennungen je Prüfung — **einmal je Lauf**.
   *
   * Ein Lauf dreht so viele Runden, bis der Rückstand weg ist; ohne diesen Zwischenspeicher
   * läse er `readExam` und `buildTaskIndex` in jeder Runde erneut, und beide gehen über die
   * ganze Prüfungsstruktur. Bei einem Rückstand von hundert Bewertungen wären das zwanzig
   * volle Durchläufe — auf einem iPad 6 nicht nebensächlich.
   *
   * ⚠️ Nur die **Prüfungsdaten** werden gemerkt, nicht der Bewertungsstand: der ändert sich
   * mit jedem Stempel und wird in jeder Runde neu gelesen.
   */
  const pruefungsCache = new Map<
    string,
    {
      index: Awaited<ReturnType<typeof buildTaskIndex>>;
      tasks: Task[];
      stationen: Station[];
      kennungen: Map<string, string>;
    }
  >();

  async function pruefungsdaten(examID: string) {
    const gemerkt = pruefungsCache.get(examID);
    if (gemerkt) return gemerkt;
    const index = await buildTaskIndex(deps.store, examID);
    const stationen = index.size === 0 ? [] : (await readExam(deps.store, examID)).stations;
    const kennungen = new Map(
      (await deps.store.all<ClassicKennungRow>('ClassicKennung', { examID })).map((k) => [k.id, k.roh]),
    );
    const frisch = { index, tasks: stationen.flatMap((s) => s.tasks), stationen, kennungen };
    pruefungsCache.set(examID, frisch);
    return frisch;
  }

  const skizzenCache = new Map<string, string | null>();

  /** Alles, was in diesem Lauf gebaut wurde — quer über alle Prüflinge. */
  async function baueStapel(): Promise<Bewertungsstapel> {
    const offen = deps.mitDaten && !deps.mitDaten() ? [] : await pendingLogs(deps.store);
    const gesamt: Bewertungsstapel = {
      taskratings: [],
      answerratings: [],
      stationratings: [],
      gruppen: [],
      liegengeblieben: [],
    };

    for (const [schlüssel, zeilen] of nachPruefling(offen)) {
      const [examID, candidateID] = schlüssel.split('|');
      const { index, tasks, kennungen } = await pruefungsdaten(examID);
      if (index.size === 0) {
        // Die Prüfung liegt nicht (mehr) auf dem Gerät — ohne ihre Aufgaben lässt sich nichts
        // zuordnen.
        for (const z of zeilen) {
          gesamt.liegengeblieben.push({
            logId: z.id,
            grund: 'Die Prüfung ' + examID + ' liegt nicht auf dem Gerät.',
            erwartet: false,
          });
        }
        continue;
      }

      const assessment = await loadAssessment(deps.store, index, examID, candidateID);

      const skizzen = new Map<string, string>();
      if (deps.skizze) {
        for (const [schluessel, kasten] of Object.entries(assessment.comments)) {
          if (kasten.handwriting.length === 0) continue;
          // Gemerkt nach Strichen: der Lauf baut alle zehn Sekunden, und eine Leinwand je Takt
          // für dieselbe Seite wäre vergeudet.
          const merk = JSON.stringify(kasten.handwriting);
          let bild = skizzenCache.get(merk);
          if (bild === undefined) {
            bild = deps.skizze(kasten.handwriting);
            skizzenCache.set(merk, bild);
          }
          if (bild) skizzen.set(schluessel, bild);
        }
      }

      // Prüfer und Schauspieler stehen in den Zeilen selbst — beim Bewerten war einer gewählt.
      const mitPrüfer = zeilen.find((z) => z.examinerID);
      const teil = baueBewertungen({
        offen: zeilen,
        assessment,
        tasks,
        index,
        kennungen,
        candidateID,
        examinerID: mitPrüfer?.examinerID ?? null,
        actorID: zeilen.find((z) => z.actorID)?.actorID ?? null,
        naechsteId,
        skizzen,
      });

      gesamt.taskratings.push(...teil.taskratings);
      gesamt.answerratings.push(...teil.answerratings);
      gesamt.stationratings.push(...teil.stationratings);
      gesamt.gruppen.push(...teil.gruppen);
      gesamt.liegengeblieben.push(...teil.liegengeblieben);
    }

    return gesamt;
  }

  /** Den Rumpf bauen — Herzschlag plus der Ausschnitt des Stapels, der diesmal mitgeht. */
  async function baueRumpf(
    stapel: Bewertungsstapel,
    taskratings: ClassicAufgabenbewertung[],
    answerratings: ClassicZeilenbewertung[],
  ): Promise<ClassicLogRumpf> {
    const [client] = await deps.store.all<ClientRow>('Client', {
      deviceIdentifier: deps.geraet.deviceID,
    });

    const examiner = await personVon(deps.store, 'Examiner', client?.currentExaminerID ?? null);
    const examinee = await personVon(deps.store, 'Candidate', client?.currentCandidateID ?? null);

    let station: ClassicLogRumpf['station'] = null;
    if (client?.currentExamID && client.currentCandidateID) {
      const stationId = await letzteStation(deps.store, client.currentExamID, client.currentCandidateID);
      const id = rohkennung(stationId);
      const osceID = rohkennung(client.currentExamID);
      if (id !== null && osceID !== null) {
        const { stationen } = await pruefungsdaten(client.currentExamID);
        const gefunden = stationen.find((s) => s.id === stationId);
        station = { id, name: gefunden?.label ?? '', osceID };
      }
    }

    const pruefungen = await deps.store.all<{ id: string; name: string; lastModifiedDate?: string }>('Exam');
    const osceID = rohkennung(client?.currentExamID ?? undefined);

    return {
      deviceID: deps.geraet.deviceID,
      deviceName: deps.geraet.deviceName,
      osces: pruefungen
        // `lastUpdate` in der 14-stelligen Form des Servers — so sieht er, welcher Stand liegt.
        .map((e) => ({ id: rohkennung(e.id), name: e.name, lastUpdate: (e.lastModifiedDate ?? '').slice(0, 14) }))
        .filter((e): e is { id: number; name: string; lastUpdate: string } => e.id !== null),
      examiner: examiner && osceID !== null ? { ...examiner, osceID } : examiner,
      examinee,
      conf: deps.conf ? await deps.conf().catch(() => ({})) : {},
      station,
      taskratings,
      answerratings,
      stationratings: stapel.stationratings,
      // ⚠️ Der **gesamte** Rückstand, nicht die Grösse dieses Stapels: daraus baut die
      // Management-Konsole ihre Anzeige „noch n unübertragen".
      totalUnsentAnswerratings: stapel.answerratings.length,
      totalUnsentTaskratings: stapel.taskratings.length,
      totalUnsentStationratings: stapel.stationratings.length,
      batteryStatus: deps.batterie ? await deps.batterie() : 100,
      brightness: deps.helligkeit ? await deps.helligkeit().catch(() => -1) : -1,
      appVersion: deps.geraet.appVersion,
      model: deps.geraet.model ?? '',
      currentTime: jetzt().toISOString(),
    };
  }

  async function lauf(): Promise<SendeErgebnis> {
    const ergebnis: SendeErgebnis = { uebertragen: 0, liegengeblieben: 0, nachricht: null };
    /** Damit dieselbe Warnung nicht bei jeder Runde erneut im Protokoll steht. */
    const schonGemeldet = new Set<string>();

    for (let runde = 0; runde < HOECHSTENS_RUNDEN; runde += 1) {
      const stapel = await baueStapel();

      const echteProbleme = stapel.liegengeblieben.filter((l) => !l.erwartet);
      for (const problem of echteProbleme) {
        if (schonGemeldet.has(problem.grund)) continue;
        schonGemeldet.add(problem.grund);
        await deps.log('Übertragung (Classic): ' + problem.grund, 'warn');
      }
      if (runde === 0) ergebnis.liegengeblieben = echteProbleme.length;

      const taskratings = stapel.taskratings.slice(0, STAPEL_TASKS);
      const answerGrenze = taskratings.length > 0 ? STAPEL_ANSWERS_MIT_TASKS : STAPEL_ANSWERS_ALLEIN;
      const answerratings = stapel.answerratings.slice(0, answerGrenze);

      let antwort: ClassicLogAntwort;
      try {
        antwort = await deps.client.post<ClassicLogAntwort>(
          '/rest/log?id=' + zufall(),
          await baueRumpf(stapel, taskratings, answerratings),
        );
      } catch (fehler) {
        // Nichts stempeln: der Stapel ist nicht angekommen. Was offen war, bleibt offen.
        // Kein Netz steht schon einmal je Offline-Phase im Log (`../verbindung.ts`) — wie bei Relay.
        if (istNetzausfall(fehler)) return ergebnis;
        await deps.log(
          'Übertragung (Classic) abgebrochen: ' + String(fehler) +
            (lohntWiederholung(fehler) ? ' — wird erneut versucht' : ' — braucht eine Entscheidung'),
        );
        return ergebnis;
      }

      if (antwort.message) ergebnis.nachricht = antwort.message;
      if (antwort.clientRequests && antwort.clientRequests.length > 0) {
        // ⚠️ **Sofort, im selben Durchgang.** Der Server verwirft die Anforderung nach dem
        // ersten Ausliefern — wer sie aufschiebt oder wegwirft, verliert sie endgültig.
        await deps.log('Der Server fordert an: ' + antwort.clientRequests.join(', '));
        if (deps.erfuelleAnforderungen) {
          try {
            await deps.erfuelleAnforderungen(antwort.clientRequests);
          } catch (fehler) {
            // ⚠️ **Kein Abbruch.** Die Quittungen dieses Durchgangs hängen nicht am Upload;
            // wer hier abbräche, liesse bereits übertragene Bewertungen als offen stehen, und
            // sie gingen beim nächsten Lauf ein zweites Mal hinaus.
            await deps.log('Anforderung des Servers gescheitert: ' + String(fehler), 'warn');
          }
        } else {
          await deps.log('Dateien hochladen kann diese Umgebung nicht — nur das Gerät.', 'warn');
        }
      }

      /** Welche unserer Kennungen quittiert sind — `originalID` **und** `lastUpdate`. */
      const quittiert = new Set<number>();
      const nimmAn = (liste: { originalID?: number; lastUpdate?: string }[] | undefined) => {
        for (const r of liste ?? []) {
          if (r.originalID === undefined || r.lastUpdate === undefined) continue;
          const gruppe = stapel.gruppen.find((g) => g.id === r.originalID);
          if (gruppe && gruppe.lastUpdate === r.lastUpdate) quittiert.add(gruppe.id);
        }
      };
      nimmAn(antwort.taskratings);
      nimmAn(antwort.answerratings);
      nimmAn(antwort.stationratings);

      /*
       * Eine Zeile ist erst durch, wenn **jedes** Rating dieses Rumpfes, zu dem sie beiträgt,
       * quittiert wurde. Gruppen, die diesmal gar nicht mitgingen (der Stapel ist begrenzt),
       * blockieren nicht — ihre Zeilen kommen in einer späteren Runde erneut dran.
       */
      const offeneNennungen = new Map<string, number>();
      for (const gruppe of stapel.gruppen) {
        if (quittiert.has(gruppe.id)) continue;
        for (const logId of gruppe.logIds) {
          offeneNennungen.set(logId, (offeneNennungen.get(logId) ?? 0) + 1);
        }
      }

      const fertig = new Set<string>();
      for (const gruppe of stapel.gruppen) {
        if (!quittiert.has(gruppe.id)) continue;
        for (const logId of gruppe.logIds) {
          if (!offeneNennungen.has(logId)) fertig.add(logId);
        }
      }

      if (fertig.size > 0) {
        await markSent(deps.store, [...fertig]);
        ergebnis.uebertragen += fertig.size;
      }

      // Weiter nur, wenn dieser Rumpf etwas bewegt hat und noch etwas Sendbares übrig ist.
      const restSendbar =
        stapel.taskratings.length > taskratings.length ||
        stapel.answerratings.length > answerratings.length;
      const etwasGeschafft = fertig.size > 0;
      if (!restSendbar || !etwasGeschafft) {
        if (restSendbar && !etwasGeschafft) {
          await deps.log(
            'Übertragung (Classic): der Server hat diesmal nichts quittiert — es bleibt liegen ' +
              'und geht beim nächsten Takt erneut hinaus.',
          );
        }
        return ergebnis;
      }
    }

    await deps.log(
      'Übertragung (Classic): nach ' + String(HOECHSTENS_RUNDEN) + ' Runden abgebrochen — der ' +
        'Rückstand geht beim nächsten Takt weiter hinaus.',
    );
    return ergebnis;
  }

  return {
    /**
     * Ein Durchgang. Takt und „Jetzt übertragen" rufen dasselbe; zwei Läufe übereinander
     * schickten denselben Stapel doppelt, deshalb bekommt der zweite Aufrufer den laufenden.
     */
    async jetztSenden(): Promise<SendeErgebnis> {
      if (laeuft) return laeuft;
      laeuft = lauf().finally(() => {
        laeuft = null;
      });
      return laeuft;
    },
    laeuftGerade: () => laeuft !== null,
  };
}
